import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, MenuItem,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TranslateIcon from '@mui/icons-material/Translate';
import { doc, updateDoc } from 'firebase/firestore';
import { fireStoreDB } from 'services';
import { getProfile, translateText } from 'utilities';
import { languagesList } from 'common';

const MAX = 1000;

/**
 * "Additional Information" free-text medical note.
 * Saved to the member's profile (medicalNotes) and shown to responders on the
 * emergency card / the page behind the QR. Styled to match the category cards.
 * Includes on-demand translation (reuses the Google Translate utility).
 */
export const MedicalNotesCard = ({ initialValue = '', onSaved }) => {
  const [text, setText] = useState(initialValue || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Translation
  const [showTranslate, setShowTranslate] = useState(false);
  const [lang, setLang] = useState('');
  const [translated, setTranslated] = useState('');
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState('');

  useEffect(() => {
    setText(initialValue || '');
  }, [initialValue]);

  const dirty = text !== (initialValue || '');

  const onTextChange = (value) => {
    setText(value.slice(0, MAX));
    // The current translation no longer matches the edited text.
    setTranslated('');
    setTranslateError('');
    setLang('');
  };

  const handleSave = async () => {
    setError('');
    const uid = getProfile()?.uid;
    if (!uid) {
      setError('Could not find your account — try signing out and back in.');
      return;
    }
    setSaving(true);
    try {
      const write = updateDoc(doc(fireStoreDB, 'user', uid), {
        medicalNotes: text.trim(),
      });
      // Never let the spinner hang forever if the write can't reach Firestore.
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('__timeout__')), 12000),
      );
      await Promise.race([write, timeout]);
      onSaved?.(text.trim());
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      console.error('Failed to save medical notes', e);
      setError(
        e?.message === '__timeout__'
          ? 'Saving is taking too long — check your connection and try again.'
          : e?.message || 'Could not save. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const runTranslate = async (target) => {
    setLang(target);
    setTranslated('');
    setTranslateError('');
    if (!target || !text.trim()) return;
    setTranslating(true);
    try {
      const result = await translateText(text.trim(), target);
      setTranslated(result);
    } catch (e) {
      console.error('Translate failed', e);
      setTranslateError('Could not translate right now. Please try again.');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        mb: '20px',
        borderRadius: '15px',
        bgcolor: '#fafcff',
        overflow: 'hidden',
      }}
    >
      {/* Header band — matches the category cards */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 80, bgcolor: '#eff3fc' }}>
        <Box sx={{ width: 80, height: 80, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
          <Box
            sx={{
              width: 50, height: 50, borderRadius: '50px', bgcolor: '#d6ecfd',
              display: 'flex', justifyContent: 'center', alignItems: 'center',
            }}
          >
            <EditNoteIcon sx={{ color: '#1E91D6', fontSize: 26 }} />
          </Box>
        </Box>
        <Box sx={{ flex: 1, py: 1.5, pr: 2 }}>
          <Typography sx={{ fontSize: 18, color: '#101828' }}>Additional Information</Typography>
          <Typography sx={{ fontSize: 13, color: '#667085' }}>
            Anything a first responder should know — shown on your emergency card when scanned.
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: '20px' }}>
        <TextField
          multiline
          minRows={4}
          maxRows={12}
          fullWidth
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder="e.g. On blood thinners, pacemaker fitted, please contact Dr. Hansen first…"
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '10px' } }}
        />

        {error && (
          <Typography sx={{ mt: 1, fontSize: 13, color: '#b91c1c' }}>{error}</Typography>
        )}

        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: text.length >= MAX ? '#b91c1c' : '#98A2B3' }}>
              {text.length}/{MAX}
            </Typography>
            {text.trim() && (
              <Button
                size="small"
                startIcon={<TranslateIcon sx={{ fontSize: 18 }} />}
                onClick={() => setShowTranslate((v) => !v)}
                sx={{ textTransform: 'none', color: '#1E91D6', minWidth: 0, fontWeight: 600 }}
              >
                Translate
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {saved && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#10b981' }}>
                <CheckCircleIcon sx={{ fontSize: 17 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Saved</Typography>
              </Box>
            )}
            <Button
              variant="contained"
              disableElevation
              disabled={saving || !dirty}
              onClick={handleSave}
              startIcon={saving && <CircularProgress size={16} color="inherit" />}
              sx={{
                textTransform: 'none', fontWeight: 500, borderRadius: '5px',
                px: 3, bgcolor: '#47b6f6', '&:hover': { bgcolor: '#1E91D6' },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>

        {/* Translate panel */}
        {showTranslate && text.trim() && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#eff3fc', borderRadius: '10px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <TranslateIcon sx={{ color: '#1E91D6' }} />
              <TextField
                select
                size="small"
                label="Translate to"
                value={lang}
                onChange={(e) => runTranslate(e.target.value)}
                sx={{ minWidth: 220, bgcolor: '#fff', borderRadius: 1 }}
              >
                <MenuItem value="">
                  <em>Choose language…</em>
                </MenuItem>
                {languagesList.map(({ code, language }) => (
                  <MenuItem key={code} value={code}>{language}</MenuItem>
                ))}
              </TextField>
              {translating && <CircularProgress size={18} />}
            </Box>

            {translateError && (
              <Typography sx={{ mt: 1.5, fontSize: 13, color: '#b91c1c' }}>{translateError}</Typography>
            )}
            {translated && !translating && (
              <Typography sx={{ mt: 1.5, fontSize: 14, color: '#101828', whiteSpace: 'pre-wrap' }}>
                {translated}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
